import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";

import apiService from "../services/api";

import { useAuth } from "./AuthContext";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }

  return context;
};

export const NotificationProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [newTicketsCount, setNewTicketsCount] = useState(0);
  // const [lastCheckedTickets, setLastCheckedTickets] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [previousUserId, setPreviousUserId] = useState(null);

  // Check for new tickets
  const checkNewTickets = useCallback(async () => {
    if (!token || !user) return;

    try {
      // Use ticket count endpoint - it returns count of tickets requiring attention
      // For admins: tickets with status 'open' or 'customer_replied'
      // For customers: tickets with status 'answered'
      const countData = await apiService.getTicketCount();

      setNewTicketsCount(countData.data?.count || 0);

      // Still fetch notifications for the dropdown
      if (countData.data?.count > 0) {
        const notificationData = await apiService.getNotifications({
          is_read: false,
          limit: 10,
        });

        setNotifications(notificationData.data?.notifications || []);
      }
    } catch (error) {
      console.error("Error checking for new tickets:", error);
    }
  }, [token, user?.id]); // Only depend on stable values to avoid infinite loop

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await apiService.markNotificationRead(notificationId);

      // Refresh count from API - don't manipulate locally
      await checkNewTickets();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Mark notifications as read by ticket ID
  const markAsReadByTicketId = async (ticketId) => {
    try {
      // Keep localStorage for UI purposes (showing viewed tickets)
      const viewedTickets = localStorage.getItem("viewedTickets");
      const viewedSet = viewedTickets
        ? new Set(JSON.parse(viewedTickets))
        : new Set();

      viewedSet.add(ticketId);
      localStorage.setItem(
        "viewedTickets",
        JSON.stringify(Array.from(viewedSet))
      );

      // Find and mark all notifications for this ticket as read via API
      const ticketNotifications = notifications.filter(
        (n) =>
          (n.ticketId === ticketId ||
            n.ticket_id === ticketId ||
            n.ticket_number === ticketId ||
            n.ticketNumber === ticketId ||
            n.relatedId === ticketId) &&
          !n.read
      );

      if (ticketNotifications.length > 0) {
        for (const notif of ticketNotifications) {
          try {
            await apiService.markNotificationRead(notif.id);
          } catch (_err) {
            // Silently fail individual notifications
          }
        }
      }

      // Refresh count from API - don't manipulate locally
      await checkNewTickets();
    } catch (error) {
      console.error("Error marking ticket as viewed:", error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsRead();

      // Refresh count from API - don't manipulate locally
      await checkNewTickets();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  // Clear all notifications
  const clearNotifications = async () => {
    try {
      await apiService.clearNotifications();

      // Refresh count from API - don't manipulate locally
      await checkNewTickets();

      return true;
    } catch (error) {
      console.error("Error clearing notifications:", error);

      return false;
    }
  };

  // Delete a single notification
  const deleteNotification = async (notificationId) => {
    try {
      await apiService.deleteNotification(notificationId);

      // Refresh count from API - don't manipulate locally
      await checkNewTickets();
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  // Dismiss banner
  const dismissBanner = () => {
    setShowNotificationBanner(false);
  };

  // Add a manual notification
  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification,
    };

    setNotifications((prev) => [newNotification, ...prev].slice(0, 50)); // Keep max 50 notifications
  };

  useEffect(() => {
    if (user && user.id !== previousUserId) {
      setNewTicketsCount(0);
      setNotifications([]);
      setShowNotificationBanner(false);
      setPreviousUserId(user.id);

      const viewedTickets = localStorage.getItem("viewedTickets");

      if (viewedTickets) {
        try {
          const parsed = JSON.parse(viewedTickets);

          if (parsed.userId !== user.id) {
            localStorage.removeItem("viewedTickets");
          }
        } catch {
          localStorage.removeItem("viewedTickets");
        }
      }
    }
  }, [user, previousUserId]);

  // Set up polling - checkNewTickets() handles both count and notifications from API
  useEffect(() => {
    if (token && user) {
      // Initial check
      checkNewTickets();

      // Use adaptive polling interval based on time and role
      const getPollingInterval = () => {
        const hour = new Date().getHours();
        const isBusinessHours = hour >= 9 && hour <= 17;
        const isAdmin = user?.role === "admin";

        if (isAdmin && isBusinessHours) return 45000; // 45s during business hours (staggered from dashboard)
        if (isAdmin) return 120000; // 2 min off-hours

        return 90000; // 1.5 min for customers
      };

      const interval = setInterval(checkNewTickets, getPollingInterval());

      // Handle tab visibility
      const handleVisibilityChange = () => {
        if (document.hidden) {
          clearInterval(interval);
        } else {
          checkNewTickets();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        clearInterval(interval);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }
  }, [token, user?.id]); // Remove checkNewTickets to avoid infinite re-renders

  const value = {
    newTicketsCount,
    notifications,
    showNotificationBanner,
    checkNewTickets,
    markAsRead,
    markAsReadByTicketId,
    markAllAsRead,
    clearNotifications,
    deleteNotification,
    dismissBanner,
    addNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
